import { useEffect, useState } from 'react'
import { Plus, Search, Filter, Video } from 'lucide-react'
import { useVideoStore } from '../stores/videoStore'
import VideoCard from '../components/VideoCard'
import LinkCreator from '../components/LinkCreator'
import VideoUpload from '../components/VideoUpload'
import { Spinner, EmptyState } from '../components/common'
import { useDebounce } from '../hooks'

export default function Videos() {
  const { videos, isLoading, error, fetchVideos, deleteVideo, page, pages, total } = useVideoStore()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null)
  const [showUploadModal, setShowUploadModal] = useState(false)

  const debouncedSearch = useDebounce(search, 300)

  useEffect(() => {
    fetchVideos({ search: debouncedSearch, status: statusFilter || undefined })
  }, [fetchVideos, debouncedSearch, statusFilter])

  const handlePageChange = (newPage: number) => {
    fetchVideos({ page: newPage, search: debouncedSearch, status: statusFilter || undefined })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Videos</h1>
          <p className="text-slate-400">{total} videos in your library</p>
        </div>

        <button onClick={() => setShowUploadModal(true)} className="btn-primary">
          <Plus className="w-4 h-4" />
          Upload Video
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="input-icon" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search videos..."
            className="input pl-10"
          />
        </div>

        <div className="relative">
          <Filter className="input-icon" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="select pl-10"
          >
            <option value="">All status</option>
            <option value="ready">Ready</option>
            <option value="processing">Processing</option>
            <option value="pending">Pending</option>
            <option value="error">Error</option>
          </select>
        </div>
      </div>

      {/* Content */}
      {isLoading && videos.length === 0 ? (
        <div className="flex items-center justify-center h-64">
          <Spinner size="lg" />
        </div>
      ) : error ? (
        <EmptyState
          title="Failed to load videos"
          description={error}
          action={
            <button onClick={() => fetchVideos()} className="btn-primary">
              Try Again
            </button>
          }
        />
      ) : videos.length === 0 ? (
        <EmptyState
          icon={<Video className="w-12 h-12" />}
          title="No videos yet"
          description="Upload your first video to get started with personalized video sharing"
          action={
            <button onClick={() => setShowUploadModal(true)} className="btn-primary">
              <Plus className="w-4 h-4" />
              Upload Video
            </button>
          }
        />
      ) : (
        <>
          {/* Video grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {videos.map((video) => (
              <VideoCard
                key={video.id}
                video={video}
                onCreateLink={() => setSelectedVideoId(video.id)}
                onDelete={() => deleteVideo(video.id)}
              />
            ))}
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page <= 1}
                className="btn-ghost disabled:opacity-50"
              >
                Previous
              </button>

              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, pages) }, (_, i) => {
                  let pageNum: number
                  if (pages <= 5) {
                    pageNum = i + 1
                  } else if (page <= 3) {
                    pageNum = i + 1
                  } else if (page >= pages - 2) {
                    pageNum = pages - 4 + i
                  } else {
                    pageNum = page - 2 + i
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`w-8 h-8 rounded-lg text-sm font-medium ${
                        page === pageNum
                          ? 'bg-primary-600 text-white'
                          : 'text-slate-400 hover:bg-slate-700'
                      }`}
                    >
                      {pageNum}
                    </button>
                  )
                })}
              </div>

              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= pages}
                className="btn-ghost disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Link creator modal */}
      {selectedVideoId && (
        <LinkCreator
          videoId={selectedVideoId}
          onClose={() => setSelectedVideoId(null)}
        />
      )}

      {/* Upload modal */}
      {showUploadModal && (
        <VideoUpload
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => fetchVideos()}
        />
      )}
    </div>
  )
}
